/**
 * themes.ts - Haunted building themes (10 themes)
 */

import type { GhostId } from './ghosts.js';
import type { RoomTypeId } from './rooms.js';
import type { HauntedThemeId } from '../state.js';

export interface HauntedThemeDef {
  id: HauntedThemeId;
  name: string;
  description: string;
  preferredRoomTypes: RoomTypeId[];
  preferredGhostIds: GhostId[];
  buildingExteriorAsset: string;
  lobbyStyleId: string;
  artPromptKey: string;
}

export const HAUNTED_THEMES: Record<HauntedThemeId, HauntedThemeDef> = {
  haunted_house: {
    id: 'haunted_house',
    name: 'Haunted House',
    description: '기본 클래식 공포 체험관',
    preferredRoomTypes: ['dark_corridor', 'mirror_room', 'graveyard_room', 'coffin_room', 'bathroom', 'kitchen', 'attic', 'secret_passage'],
    preferredGhostIds: ['jinaeryeong', 'vampire', 'banshee', 'blood_count', 'well_spirit'],
    buildingExteriorAsset: '/images/views/building-exterior-haunted-house.webp',
    lobbyStyleId: 'classic_haunted_lobby',
    artPromptKey: 'theme_haunted_house_flux',
  },
  abandoned_hospital: {
    id: 'abandoned_hospital',
    name: 'Abandoned Hospital',
    description: '의료 공포와 실험실 분위기의 병원 테마',
    preferredRoomTypes: ['lab', 'bathroom', 'mirror_room', 'dungeon', 'dark_corridor', 'morgue', 'surgery_room'],
    preferredGhostIds: ['mummy', 'jinaeryeong', 'grim_reaper', 'nightmare_specter'],
    buildingExteriorAsset: '/images/views/building-exterior-abandoned-hospital.webp',
    lobbyStyleId: 'hospital_lobby',
    artPromptKey: 'theme_abandoned_hospital_flux',
  },
  school_back_mountain: {
    id: 'school_back_mountain',
    name: 'School Back Mountain',
    description: '학교 괴담과 산길 공포를 섞은 테마',
    preferredRoomTypes: ['library', 'bathroom', 'dark_corridor', 'mirror_room', 'doll_room', 'music_room', 'science_lab'],
    preferredGhostIds: ['jinaeryeong', 'banshee', 'well_spirit', 'shadow_stalker'],
    buildingExteriorAsset: '/images/views/building-exterior-school-back-mountain.webp',
    lobbyStyleId: 'school_lobby',
    artPromptKey: 'theme_school_back_mountain_flux',
  },
  cursed_circus: {
    id: 'cursed_circus',
    name: 'Cursed Circus',
    description: '저주받은 서커스 텐트의 기괴한 공포',
    preferredRoomTypes: ['mirror_room', 'doll_room', 'dark_corridor', 'funhouse', 'trapeze_room', 'freak_show'],
    preferredGhostIds: ['pierrot', 'banshee', 'nightmare_specter'],
    buildingExteriorAsset: '/images/views/building-exterior-cursed-circus.webp',
    lobbyStyleId: 'circus_lobby',
    artPromptKey: 'theme_cursed_circus_flux',
  },
  ancient_temple: {
    id: 'ancient_temple',
    name: 'Ancient Temple',
    description: '고대 사원의 신비롭고 섬뜩한 저주',
    preferredRoomTypes: ['sacrifice_altar', 'treasure_vault', 'dark_corridor', 'underground_river', 'curse_chamber', 'mummy_tomb'],
    preferredGhostIds: ['mummy', 'gangsi', 'grim_reaper'],
    buildingExteriorAsset: '/images/views/building-exterior-ancient-temple.webp',
    lobbyStyleId: 'temple_lobby',
    artPromptKey: 'theme_ancient_temple_flux',
  },
  pirate_ship: {
    id: 'pirate_ship',
    name: 'Pirate Ship',
    description: '저주받은 해적선의 바다 공포',
    preferredRoomTypes: ['captain_cabin', 'cargo_hold', 'dark_corridor', 'davy_jones_locker', 'brig', 'crows_nest'],
    preferredGhostIds: ['vampire', 'gangsi', 'werewolf'],
    buildingExteriorAsset: '/images/views/building-exterior-pirate-ship.webp',
    lobbyStyleId: 'pirate_lobby',
    artPromptKey: 'theme_pirate_ship_flux',
  },
  abandoned_mine: {
    id: 'abandoned_mine',
    name: 'Abandoned Mine',
    description: '버려진 광산의 깊고 어두운 공포',
    preferredRoomTypes: ['mine_shaft', 'crystal_cave', 'dark_corridor', 'explosives_storage', 'underground_lake', 'rail_tunnel'],
    preferredGhostIds: ['werewolf', 'grim_reaper', 'shadow_stalker'],
    buildingExteriorAsset: '/images/views/building-exterior-abandoned-mine.webp',
    lobbyStyleId: 'mine_lobby',
    artPromptKey: 'theme_abandoned_mine_flux',
  },
  gothic_castle: {
    id: 'gothic_castle',
    name: 'Gothic Castle',
    description: '고딕 성의 우아하고 섬뜩한 귀족 공포',
    preferredRoomTypes: ['throne_room', 'dungeon', 'library', 'mirror_room', 'wine_cellar', 'tower_chamber'],
    preferredGhostIds: ['vampire', 'blood_count', 'banshee'],
    buildingExteriorAsset: '/images/views/building-exterior-gothic-castle.webp',
    lobbyStyleId: 'castle_lobby',
    artPromptKey: 'theme_gothic_castle_flux',
  },
  underground_bunker: {
    id: 'underground_bunker',
    name: 'Underground Bunker',
    description: '지하 벙커의 실험과 음모 공포',
    preferredRoomTypes: ['lab', 'server_room', 'dark_corridor', 'bathroom', 'quarantine_zone', 'armory'],
    preferredGhostIds: ['nightmare_specter', 'mummy', 'grim_reaper'],
    buildingExteriorAsset: '/images/views/building-exterior-underground-bunker.webp',
    lobbyStyleId: 'bunker_lobby',
    artPromptKey: 'theme_underground_bunker_flux',
  },
  cursed_forest: {
    id: 'cursed_forest',
    name: 'Cursed Forest',
    description: '저주받은 숲의 자연령과 원시 공포',
    preferredRoomTypes: ['witch_hut', 'treehouse', 'dark_corridor', 'swamp_path', 'fairy_ring', 'ancient_ruin'],
    preferredGhostIds: ['well_spirit', 'shadow_stalker', 'kappa', 'werewolf'],
    buildingExteriorAsset: '/images/views/building-exterior-cursed-forest.webp',
    lobbyStyleId: 'forest_lobby',
    artPromptKey: 'theme_cursed_forest_flux',
  },
};
